import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, Plus, Building2, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSchools } from '@/hooks/use-schools';
import { useSearchUsers } from '@/hooks/use-users';
import { SchoolLogo } from '@/components/school-logo';
import { UserDetailDialog } from '@/components/users/user-detail-dialog';
import type { User, UserSearchResult } from '@/types/user';

type SearchMode = 'schools' | 'users';

export function MapHeader() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [searchMode, setSearchMode] = useState<SearchMode>('schools');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch schools when there's a search query and mode is schools
  const { data: schoolsData, isLoading: isLoadingSchools } = useSchools({
    search: searchMode === 'schools' && debouncedQuery ? debouncedQuery : undefined,
    limit: 10,
    paginate: true,
  });

  // Fetch users when there's a search query and mode is users
  const { data: usersData = [], isLoading: isLoadingUsers } = useSearchUsers(
    searchMode === 'users' && debouncedQuery.length >= 4 ? debouncedQuery : ''
  );

  const isLoading = searchMode === 'schools' ? isLoadingSchools : isLoadingUsers;

  // Show/hide results based on query and focus
  useEffect(() => {
    setShowResults(searchQuery.length > 0);
  }, [searchQuery]);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSchoolSelect = (schoolName: string) => {
    setSearchQuery('');
    setShowResults(false);
    navigate(`/dashboard/schools/${schoolName}`);
  };

  const handleUserSelect = (user: UserSearchResult) => {
    setSearchQuery('');
    setShowResults(false);
    // Convert UserSearchResult to User type for the dialog
    const fullUser: User = {
      ...user,
      isAmbassador: false, // We don't have this in search results
    };
    setSelectedUser(fullUser);
    setUserDialogOpen(true);
  };

  const toggleSearchMode = () => {
    const newMode = searchMode === 'schools' ? 'users' : 'schools';
    setSearchMode(newMode);
    setSearchQuery('');
    setShowResults(false);
  };

  const handleCreateSchool = () => {
    navigate('/dashboard/schools/new');
  };

  return (
    <>
      <div className="absolute left-10 top-6 z-20 flex items-center gap-2">
        {/* Search Bar */}
        <div className="relative" ref={searchRef}>
          <div className="relative flex items-center gap-2 rounded-lg bg-white/95 dark:bg-background/95 p-2 shadow-xl backdrop-blur-sm border border-border/50">
            <button
              onClick={toggleSearchMode}
              className="h-8 w-8 flex items-center justify-center rounded hover:bg-accent transition-colors flex-shrink-0"
              title={`Switch to ${searchMode === 'schools' ? 'users' : 'schools'}`}
            >
              {searchMode === 'schools' ? (
                <Building2 className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Users className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            <Input
              type="search"
              placeholder={searchMode === 'schools' ? 'Search schools...' : 'Search users...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchQuery.length > 0 && setShowResults(true)}
              className="w-[280px] h-8 bg-white dark:bg-background border-input"
            />
          </div>

          {/* Search Results Dropdown */}
          {showResults && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-popover border rounded-md shadow-lg z-50 max-h-[300px] overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Searching...
                </div>
              ) : searchMode === 'schools' ? (
                // Schools results
                schoolsData?.schools && schoolsData.schools.length > 0 ? (
                  <div className="py-1">
                    {schoolsData.schools.map((school) => (
                      <button
                        key={school.schoolName}
                        onClick={() => handleSchoolSelect(school.schoolName)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors flex items-center gap-2"
                      >
                        <SchoolLogo school={school} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{school.schoolTitle}</div>
                          {(school.state || school.country) && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {[school.state, school.country].filter(Boolean).join(', ')}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-sm text-muted-foreground text-center">
                    No schools found
                  </div>
                )
              ) : (
                // Users results
                searchQuery.length < 4 ? (
                  <div className="p-4 text-sm text-muted-foreground text-center">
                    Enter at least 4 characters
                  </div>
                ) : usersData.length > 0 ? (
                  <div className="py-1">
                    {usersData.slice(0, 10).map((user) => (
                      <button
                        key={user.id}
                        onClick={() => handleUserSelect(user)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                      >
                        <div className="font-medium">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          {user.email && <span>{user.email}</span>}
                          {user.schoolId && (
                            <Badge variant="secondary" className="text-xs px-1.5 py-0">
                              {user.schoolId}
                            </Badge>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-sm text-muted-foreground text-center">
                    No users found
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Create School Button */}
        <Button
          onClick={handleCreateSchool}
          size="sm"
          className="h-10 w-10 rounded-lg shadow-xl p-0"
          title="Create School"
        >
          <Plus className="h-5 w-5" />
        </Button>
      </div>

      {/* User Detail Dialog */}
      <UserDetailDialog user={selectedUser} open={userDialogOpen} onOpenChange={setUserDialogOpen} />
    </>
  );
}

